import type { PostHog } from "posthog-js";

// ---------------------------------------------------------------------------
// Analytics seam (ADR-0050). The ONE module that touches `posthog-js`; every
// component / route imports THIS seam, never the SDK directly. The SDK is
// pulled in via a DYNAMIC import inside `init()` — only when analytics is
// actually on — so posthog-js stays out of the entry/route bundles (and out of
// the test import graph). Posture: consent-free cookieless product analytics on
// a legitimate-interest basis — `persistence: 'memory'` (no cookies, no device
// storage → the PKE device-storage consent trigger never fires), no
// `identify()` while the funnel is guest-only, autocapture with PII masking, IP
// discarded at the PostHog project. A blank key (dev / test / any self-host
// without analytics) OR the functional opt-out flag makes the whole thing a
// NO-OP with ZERO network — the tests never touch PostHog.
// ---------------------------------------------------------------------------

// The v1 funnel taxonomy (ADR-0050). The client funnel terminates at
// `payment_initiated` — the Stripe redirect resets the cookieless memory
// session, so `order_paid` is fired server-side instead (#311). Typing the
// event name to this union keeps call sites honest: the only named events that
// flow through the seam are these seven; click-level events are autocaptured.
export type AnalyticsEvent =
  | "upload_started"
  | "preview_ready"
  | "configurator_opened"
  | "size_selected"
  | "checkout_started"
  | "delivery_selected"
  | "payment_initiated";

// Zero-PII property shape (ADR-0050): ids / enums / amounts only — never email,
// name, or address. Scalars only, so a nested object can never smuggle PII in.
export type AnalyticsProps = Record<string, string | number | boolean | undefined>;

// The single functional flag that suppresses analytics (ADR-0050). Storing a
// *suppression preference* is permitted without consent; it is read on boot and
// persisted on opt-out. Namespaced so it never collides with other app keys.
export const OPTOUT_STORAGE_KEY = "pufimi.analytics.optout";

// Read at call time (not module load) so the value reflects the deploy-injected
// env and tests can stub it per case. Blank locally → analytics off.
function posthogKey(): string {
  return (import.meta.env.VITE_PUBLIC_POSTHOG_KEY ?? "").trim();
}
function posthogHost(): string {
  return (import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "").trim();
}

// localStorage can throw (Safari private mode) or be absent (SSR) — a storage
// read/write must never break the app or the funnel.
function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** True when the visitor has opted out (the functional flag). Read on boot. */
export function isOptedOut(): boolean {
  return storage()?.getItem(OPTOUT_STORAGE_KEY) === "1";
}

// `initialized` guards `init` to run at most once (idempotent / StrictMode-safe);
// `ph` is the live PostHog instance (null until loaded); `enabled` is true only
// once PostHog is actually capturing.
let initialized = false;
let enabled = false;
let ph: PostHog | null = null;

// Dedup set for `captureOnce` — survives React StrictMode's double-invoked
// effects AND route revisits, so a per-node funnel event fires once per key.
const firedOnce = new Set<string>();

/**
 * Cookieless, idempotent lazy-init (ADR-0050). A no-op — and ZERO network —
 * when the key is blank OR the visitor has opted out; otherwise it lazily loads
 * `posthog-js` and initializes it. Safe to call on every boot and under
 * StrictMode's double render. Returns the in-flight promise so callers (tests)
 * can await the load; `main.tsx` fire-and-forgets it.
 */
export async function init(): Promise<void> {
  if (initialized) return;
  initialized = true; // set synchronously → concurrent/StrictMode calls no-op
  const key = posthogKey();
  if (!key || isOptedOut()) return;
  try {
    const { default: posthog } = await import("posthog-js");
    posthog.init(key, {
      api_host: posthogHost(), // first-party `/re` reverse proxy (#307)
      ui_host: "https://eu.posthog.com", // EU cloud, for app links only
      persistence: "memory", // cookieless → no device-storage consent trigger
      autocapture: true, // click-level autocapture
      person_profiles: "never", // guest-only funnel; never identify()
      disable_session_recording: true, // session replay is out of v1 scope
      mask_all_element_attributes: true, // input masking: never capture attrs/values
      mask_personal_data_properties: true, // scrub PII from properties + $current_url
    });
    ph = posthog;
    enabled = true;
  } catch {
    // Analytics must never break the app — a failed SDK load leaves it simply off.
  }
}

/** Forward a funnel event with its (zero-PII) props. No-op when not enabled. */
export function capture(name: AnalyticsEvent, props?: AnalyticsProps): void {
  if (!enabled || !ph) return;
  ph.capture(name, props);
}

/**
 * Fire a funnel event at most once per `key`. Dedups React StrictMode's
 * double-invoked effects and route revisits, so a node-scoped event (e.g.
 * `preview_ready:<jobId>`) is counted exactly once. The dedup key is consumed
 * only once analytics is live, so an event that fires before the lazy load
 * resolves is not silently swallowed — the next render fires it.
 */
export function captureOnce(key: string, name: AnalyticsEvent, props?: AnalyticsProps): void {
  if (!enabled) return;
  if (firedOnce.has(key)) return;
  firedOnce.add(key);
  capture(name, props);
}

/**
 * Persist the opt-out preference (read on the next boot) and stop capturing
 * immediately — including autocapture — for the rest of this session.
 */
export function optOut(): void {
  storage()?.setItem(OPTOUT_STORAGE_KEY, "1");
  if (ph) ph.opt_out_capturing();
  enabled = false;
}
